import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Plus, Users, CalendarDays, Receipt, Building2, Star, X, LogOut, MessageCircle, Settings as SettingsIcon, Loader2, RefreshCw, CheckCircle2, Pencil, Ban, RotateCcw, UserCog, Eraser, Trash2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { WhatsappStatusPill } from "@/pages/WhatsappSettings";

const PLAN_LABELS = {
  free: "Free",
  trial: "Trial",
  pro: "Pro",
  pro_lifetime: "Pro · Lifetime",
  business: "Business",
};

const BILLING_BADGE = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  trial: "bg-amber-100 text-amber-800 border-amber-200",
  founding: "bg-violet-100 text-violet-800 border-violet-200",
  suspended: "bg-red-100 text-red-800 border-red-200",
};

export default function SuperadminOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [waEditing, setWaEditing] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [suspendingOrg, setSuspendingOrg] = useState(null);
  const [resettingOrg, setResettingOrg] = useState(null);
  const [deletingOrg, setDeletingOrg] = useState(null);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const refresh = () => {
    setLoading(true);
    api.get("/admin/organizations")
      .then((r) => setOrgs(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="space-y-6" data-testid="superadmin-organizations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider font-semibold mb-2">
            <Building2 className="w-3 h-3" /> Painel Do IT Happen · Superadmin
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">Organizações</h1>
          <p className="text-sm text-zinc-500 mt-1">{orgs.length} agências na plataforma PromoCrew.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="new-organization-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Nova organização
          </button>
          <button
            data-testid="logout-btn-superadmin"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-700 hover:bg-zinc-50 px-3 py-2 rounded-lg text-sm"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">A carregar…</div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Agência</th>
                <th className="px-4 py-3 text-left font-medium">Slug</th>
                <th className="px-4 py-3 text-left font-medium">Plano</th>
                <th className="px-4 py-3 text-left font-medium">Estado</th>
                <th className="px-4 py-3 text-left font-medium">WhatsApp</th>
                <th className="px-4 py-3 text-right font-medium">Users</th>
                <th className="px-4 py-3 text-right font-medium">Promotores</th>
                <th className="px-4 py-3 text-right font-medium">Eventos</th>
                <th className="px-4 py-3 text-right font-medium">Recibos</th>
                <th className="px-4 py-3 text-right font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id} data-testid={`org-row-${o.slug}`} className={`border-b border-zinc-100 last:border-0 hover:bg-zinc-50 ${o.status === "suspended" ? "bg-red-50/40 text-zinc-500" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-semibold"
                        style={{ background: o.primary_color || "#0a0a0a" }}
                      >
                        {(o.name || "P").slice(0, 1)}
                      </div>
                      <div>
                        <div className="font-medium text-zinc-900 inline-flex items-center gap-1.5">
                          {o.name}
                          {o.is_founding_customer && (
                            <span data-testid={`founding-${o.slug}`} title="Founding customer">
                              <Star className="w-3.5 h-3.5 text-violet-500 fill-violet-500" />
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500">{o.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">/{o.slug}</td>
                  <td className="px-4 py-3 text-zinc-700">{PLAN_LABELS[o.plan] || o.plan}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${BILLING_BADGE[o.billing_status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                      {o.billing_status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`org-wa-${o.slug}`}
                      onClick={() => setWaEditing(o)}
                      className="inline-flex items-center gap-1.5 hover:opacity-80"
                      title="Editar WhatsApp"
                    >
                      <WhatsappStatusPill status={o.whatsapp?.status || "not_configured"} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-zinc-900">
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3 text-zinc-400" />{o.stats?.users ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-zinc-900">{o.stats?.promoters ?? 0}</td>
                  <td className="px-4 py-3 text-right font-medium text-zinc-900">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3 text-zinc-400" />{o.stats?.events ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-zinc-900">
                    <span className="inline-flex items-center gap-1"><Receipt className="w-3 h-3 text-zinc-400" />{o.stats?.invoices ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link
                        to={`/${o.slug}/utilizadores`}
                        data-testid={`org-users-${o.slug}`}
                        className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:bg-zinc-100 px-2 py-1 rounded"
                        title="Gerir utilizadores (reset password, convidar)"
                      >
                        <UserCog className="w-3.5 h-3.5" /> Users
                      </Link>
                      <Link
                        to={`/admin/organizations/${o.id}/whatsapp/manual`}
                        data-testid={`org-wa-manual-${o.slug}`}
                        className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:bg-zinc-100 px-2 py-1 rounded"
                        title="WhatsApp — configuração avançada / manual (superadmin)"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </Link>
                      <button
                        data-testid={`edit-org-${o.slug}`}
                        onClick={() => setEditingOrg(o)}
                        className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:bg-zinc-100 px-2 py-1 rounded"
                        title="Editar agência"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                      {o.status === "suspended" ? (
                        <button
                          data-testid={`reactivate-org-${o.slug}`}
                          onClick={async () => {
                            if (!window.confirm(`Reativar ${o.name}?`)) return;
                            await api.post(`/admin/organizations/${o.id}/reactivate`);
                            refresh();
                          }}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded"
                          title="Reativar agência"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Reativar
                        </button>
                      ) : o.is_founding_customer ? null : (
                        <button
                          data-testid={`suspend-org-${o.slug}`}
                          onClick={() => setSuspendingOrg(o)}
                          className="inline-flex items-center gap-1 text-xs text-red-700 hover:bg-red-50 px-2 py-1 rounded"
                          title="Suspender agência"
                        >
                          <Ban className="w-3.5 h-3.5" /> Suspender
                        </button>
                      )}
                      <button
                        data-testid={`reset-org-${o.slug}`}
                        onClick={() => setResettingOrg(o)}
                        className="inline-flex items-center gap-1 text-xs text-amber-800 hover:bg-amber-50 px-2 py-1 rounded"
                        title="Limpar dados operacionais (preserva agência + users + provider configs)"
                      >
                        <Eraser className="w-3.5 h-3.5" /> Reset
                      </button>
                      {!o.is_platform_owner && !o.is_founding_customer && (
                        <button
                          data-testid={`delete-org-${o.slug}`}
                          onClick={() => setDeletingOrg(o)}
                          className="inline-flex items-center gap-1 text-xs text-red-800 hover:bg-red-100 px-2 py-1 rounded"
                          title="Eliminar agência (irreversível)"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Apagar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {orgs.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-zinc-500">Nenhuma agência ainda.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateOrgModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
      {waEditing && <WhatsappAdminModal org={waEditing} onClose={() => setWaEditing(null)} onChanged={() => { setWaEditing(null); refresh(); }} />}
      {editingOrg && <EditOrgModal org={editingOrg} onClose={() => setEditingOrg(null)} onSaved={() => { setEditingOrg(null); refresh(); }} />}
      {suspendingOrg && <SuspendOrgModal org={suspendingOrg} onClose={() => setSuspendingOrg(null)} onDone={() => { setSuspendingOrg(null); refresh(); }} />}
      {resettingOrg && <ResetOrgModal org={resettingOrg} onClose={() => setResettingOrg(null)} onDone={() => { setResettingOrg(null); refresh(); }} />}
      {deletingOrg && <DeleteOrgModal org={deletingOrg} onClose={() => setDeletingOrg(null)} onDone={() => { setDeletingOrg(null); refresh(); }} />}
    </div>
  );
}


function ResetOrgModal({ org, onClose, onDone }) {
  const [typed, setTyped] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const canConfirm = typed.trim() === org.slug;

  const submit = async (e) => {
    e.preventDefault();
    if (!canConfirm) return;
    setSaving(true); setError("");
    try {
      const r = await api.post(`/admin/organizations/${org.id}/reset-data`, { confirm_slug: org.slug });
      setResult(r.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erro ao limpar dados.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/70 z-50 flex items-center justify-center p-4" data-testid="reset-org-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-800">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Eraser className="w-5 h-5 text-amber-700" />
          <h3 className="text-lg font-display font-semibold text-zinc-950">Limpar dados operacionais</h3>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Apaga todos os eventos, promotoras, clientes, faturas, mensagens e logs.
          <strong className="text-zinc-800"> Preserva</strong> a agência, os utilizadores e as configurações de provider (Meta Cloud / Twilio / Z-API).
        </p>

        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>Esta acção é <strong>irreversível</strong>. Para confirmar, escreve o slug da agência abaixo.</div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Escreve <code className="font-mono bg-zinc-100 px-1.5 py-0.5 rounded text-zinc-900">{org.slug}</code>
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid="reset-org-confirm-input"
                className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                autoFocus
              />
            </div>
            {error && <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <div className="flex gap-2">
              <button type="button" onClick={onClose}
                      className="flex-1 border border-zinc-300 text-zinc-800 hover:bg-zinc-50 px-4 py-2.5 rounded-lg text-sm font-semibold">
                Cancelar
              </button>
              <button type="submit" disabled={!canConfirm || saving} data-testid="reset-org-submit"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 disabled:opacity-40 text-white px-4 py-2.5 rounded-lg text-sm font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eraser className="w-4 h-4" />}
                Limpar dados
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Dados de <strong>{org.slug}</strong> limpos com sucesso.
            </div>
            <div className="text-xs text-zinc-600 max-h-52 overflow-auto border border-zinc-200 rounded-lg p-3 font-mono">
              {Object.entries(result.deleted || {}).map(([k, v]) => (
                v > 0 ? <div key={k}>{k.padEnd(25, " ")} <span className="text-red-700">{v}</span></div> : null
              ))}
            </div>
            <button onClick={onDone}
                    className="w-full bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2.5 rounded-lg text-sm font-semibold">
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function DeleteOrgModal({ org, onClose, onDone }) {
  const [typed, setTyped] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const canConfirm = typed.trim() === org.slug;

  const submit = async (e) => {
    e.preventDefault();
    if (!canConfirm) return;
    setSaving(true); setError("");
    try {
      await api.delete(`/admin/organizations/${org.id}`, { data: { confirm_slug: org.slug } });
      onDone();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erro ao eliminar agência.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/70 z-50 flex items-center justify-center p-4" data-testid="delete-org-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-800">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Trash2 className="w-5 h-5 text-red-700" />
          <h3 className="text-lg font-display font-semibold text-zinc-950">Eliminar agência</h3>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Apaga <strong className="text-zinc-800">tudo</strong>: agência, utilizadores, configurações, dados operacionais.
          A agência <strong className="text-zinc-800">{org.name}</strong> desaparece completamente.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>Esta acção é <strong>irreversível e permanente</strong>. Todos os dados serão perdidos.</div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Escreve <code className="font-mono bg-zinc-100 px-1.5 py-0.5 rounded text-zinc-900">{org.slug}</code> para confirmar
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              data-testid="delete-org-confirm-input"
              className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500"
              autoFocus
            />
          </div>
          {error && <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
                    className="flex-1 border border-zinc-300 text-zinc-800 hover:bg-zinc-50 px-4 py-2.5 rounded-lg text-sm font-semibold">
              Cancelar
            </button>
            <button type="submit" disabled={!canConfirm || saving} data-testid="delete-org-submit"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white px-4 py-2.5 rounded-lg text-sm font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Eliminar agência
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SuspendOrgModal({ org, onClose, onDone }) {
  const [typed, setTyped] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const canConfirm = typed.trim() === org.slug;

  const submit = async (e) => {
    e.preventDefault();
    if (!canConfirm) return;
    setSaving(true); setError("");
    try {
      await api.post(`/admin/organizations/${org.id}/suspend`);
      onDone();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erro ao suspender.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="suspend-org-modal" className="bg-white rounded-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <h2 className="text-lg font-display font-semibold text-red-900 inline-flex items-center gap-2"><Ban className="w-5 h-5" /> Suspender agência</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-900 space-y-1">
            <div className="font-semibold">Estás prestes a suspender <strong>{org.name}</strong>.</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Todos os utilizadores da agência ficam <strong>bloqueados</strong> (login e API)</li>
              <li>Envios WhatsApp / recibos / operações ficam parados</li>
              <li>Dados são <strong>preservados</strong>: podes reactivar a qualquer momento</li>
              <li>Superadmin continua a poder aceder</li>
            </ul>
          </div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700">
              Para confirmar, escreve o slug <code className="bg-zinc-100 px-1 rounded text-red-700 font-semibold">{org.slug}</code>
            </label>
            <input
              data-testid="suspend-org-confirm"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm font-mono"
              placeholder={org.slug}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-zinc-200 rounded-lg text-sm">Cancelar</button>
            <button
              data-testid="suspend-org-submit"
              type="submit"
              disabled={!canConfirm || saving}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
              Suspender agência
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditOrgModal({ org, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: org.name || "",
    primary_color: org.primary_color || "#0a0a0a",
    logo_url: org.logo_url || "",
    company_nif: org.company_nif || "",
    status: org.status || "active",
    plan: org.plan || "trial",
    billing_status: org.billing_status || "trial",
    is_founding_customer: !!org.is_founding_customer,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.put(`/admin/organizations/${org.id}`, form);
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erro ao guardar.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="edit-org-modal" className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-display font-semibold text-zinc-900">Editar agência · {org.name}</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Slug <code className="bg-zinc-100 px-1 rounded">{org.slug}</code> é imutável.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4 overflow-y-auto">
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Nome</label>
              <input data-testid="edit-org-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Cor primária</label>
              <input data-testid="edit-org-color" type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="form-input h-10" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Logo URL</label>
              <input data-testid="edit-org-logo" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="form-input" placeholder="https://…/logo.png" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">NIF da empresa</label>
              <input data-testid="edit-org-nif" value={form.company_nif} onChange={(e) => setForm({ ...form, company_nif: e.target.value })} className="form-input" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Estado</label>
              <select data-testid="edit-org-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="form-input">
                <option value="active">active</option>
                <option value="suspended">suspended</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Plano</label>
              <select data-testid="edit-org-plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="form-input">
                <option value="trial">Trial</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="pro_lifetime">Pro · Lifetime</option>
                <option value="business">Business</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Estado de faturação</label>
              <select data-testid="edit-org-billing" value={form.billing_status} onChange={(e) => setForm({ ...form, billing_status: e.target.value })} className="form-input">
                <option value="trial">trial</option>
                <option value="active">active</option>
                <option value="founding">founding</option>
                <option value="suspended">suspended</option>
              </select>
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-zinc-700">
            <input data-testid="edit-org-founding" type="checkbox" checked={form.is_founding_customer} onChange={(e) => setForm({ ...form, is_founding_customer: e.target.checked })} />
            Founding customer (badge especial)
          </label>

          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-zinc-200 rounded-lg text-sm">Cancelar</button>
            <button data-testid="edit-org-save" type="submit" disabled={saving} className="px-4 py-2 bg-zinc-950 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function WhatsappAdminModal({ org, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get(`/admin/organizations/${org.id}/whatsapp-settings`).then((r) => {
      setData(r.data);
      setForm({
        status: r.data.status,
        onboarding_mode: r.data.onboarding_mode,
        waba_id: r.data.waba_id || "",
        phone_number_id: r.data.phone_number_id || "",
        business_account_id: r.data.business_account_id || "",
        business_id: r.data.business_id || "",
        app_id: r.data.app_id || "",
        access_token: "",
        app_secret: "",
        display_phone_number: r.data.display_phone_number || "",
        verified_name: r.data.verified_name || "",
        quality_rating: r.data.quality_rating || "",
        messaging_limit_tier: r.data.messaging_limit_tier || "",
        error_message: r.data.error_message || "",
        is_demo_connection: r.data.is_demo_connection || false,
      });
      setLoading(false);
    });
  }, [org.id]);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...form };
      // Strip empty strings so backend doesn't overwrite stored secrets with "" 
      ["access_token", "app_secret"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      const { data: fresh } = await api.put(`/admin/organizations/${org.id}/whatsapp-settings`, payload);
      setData(fresh);
      onChanged();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Erro ao guardar.");
    } finally { setSaving(false); }
  };

  const sync = async () => {
    setSaving(true);
    try {
      const { data: fresh } = await api.post(`/admin/organizations/${org.id}/whatsapp/sync-status`);
      setData(fresh);
    } finally { setSaving(false); }
  };

  const disable = async () => {
    if (!window.confirm(`Desativar WhatsApp da agência ${org.name}?`)) return;
    setSaving(true);
    try {
      await api.post(`/admin/organizations/${org.id}/whatsapp/disable`);
      onChanged();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="wa-admin-modal" className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-display font-semibold text-zinc-900 inline-flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-600" /> WhatsApp · {org.name}</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Cada agência usa o seu próprio canal. Atualiza manualmente os IDs Meta.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> A carregar…</div>
        ) : (
          <div className="p-5 space-y-4 overflow-y-auto">
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <WaField label="Status">
                <select data-testid="wa-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="form-input">
                  <option value="not_configured">not_configured</option>
                  <option value="onboarding">onboarding</option>
                  <option value="pending_meta">pending_meta</option>
                  <option value="connected">connected</option>
                  <option value="disabled">disabled</option>
                  <option value="error">error</option>
                </select>
              </WaField>
              <WaField label="Onboarding mode">
                <select value={form.onboarding_mode} onChange={(e) => setForm({ ...form, onboarding_mode: e.target.value })} className="form-input">
                  <option value="manual">manual</option>
                  <option value="embedded_signup">embedded_signup (P2.4)</option>
                </select>
              </WaField>
              <WaField label="WABA ID"><input data-testid="wa-form-waba-id" value={form.waba_id} onChange={(e) => setForm({ ...form, waba_id: e.target.value })} className="form-input" /></WaField>
              <WaField label="Phone Number ID"><input data-testid="wa-form-phone-number-id" value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })} className="form-input" /></WaField>
              <WaField label="Display phone number"><input data-testid="wa-form-display-phone" value={form.display_phone_number} onChange={(e) => setForm({ ...form, display_phone_number: e.target.value })} className="form-input" placeholder="+351911111111" /></WaField>
              <WaField label="Verified name"><input data-testid="wa-form-verified-name" value={form.verified_name} onChange={(e) => setForm({ ...form, verified_name: e.target.value })} className="form-input" /></WaField>
              <WaField label="Business ID"><input value={form.business_id} onChange={(e) => setForm({ ...form, business_id: e.target.value })} className="form-input" /></WaField>
              <WaField label="App ID"><input value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} className="form-input" /></WaField>
              <WaField label="Quality rating"><input value={form.quality_rating} onChange={(e) => setForm({ ...form, quality_rating: e.target.value })} className="form-input" placeholder="GREEN" /></WaField>
              <WaField label="Messaging tier"><input value={form.messaging_limit_tier} onChange={(e) => setForm({ ...form, messaging_limit_tier: e.target.value })} className="form-input" placeholder="TIER_1K" /></WaField>
              <WaField label="Access token (deixar vazio = manter)"><input type="password" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} className="form-input" placeholder={data.has_access_token ? "•••• guardado" : ""} /></WaField>
              <WaField label="App secret (deixar vazio = manter)"><input type="password" value={form.app_secret} onChange={(e) => setForm({ ...form, app_secret: e.target.value })} className="form-input" placeholder={data.has_app_secret ? "•••• guardado" : ""} /></WaField>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-zinc-700"><input data-testid="wa-form-demo" type="checkbox" checked={form.is_demo_connection} onChange={(e) => setForm({ ...form, is_demo_connection: e.target.checked })} /> Demo / Sandbox</label>
            <WaField label="Error message"><textarea rows={2} value={form.error_message} onChange={(e) => setForm({ ...form, error_message: e.target.value })} className="form-input" /></WaField>
            <div className="text-xs text-zinc-500">last_sync_at: {data.last_sync_at || "—"} · connected_at: {data.connected_at || "—"}</div>
          </div>
        )}

        <div className="p-4 border-t border-zinc-100 flex justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            <button data-testid="wa-sync-btn" onClick={sync} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-200 rounded-lg text-xs hover:bg-zinc-50"><RefreshCw className={`w-3.5 h-3.5 ${saving ? "animate-spin" : ""}`} /> Sync status</button>
            <button data-testid="wa-disable-btn" onClick={disable} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-700 rounded-lg text-xs hover:bg-red-50">Desativar</button>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-zinc-200 rounded-lg text-sm">Fechar</button>
            <button data-testid="wa-save-btn" onClick={save} disabled={saving} className="px-4 py-2 bg-zinc-950 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WaField({ label, children, testid }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">{label}</label>
      {children}
    </div>
  );
}

function CreateOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    slug: "",
    name: "",
    primary_color: "#0a0a0a",
    plan: "trial",
    billing_status: "trial",
    is_founding_customer: false,
    admin_email: "",
    admin_password: "",
    admin_name: "Admin",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post("/admin/organizations", form);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao criar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="create-org-modal" className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <h2 className="text-lg font-display font-semibold text-zinc-900">Nova organização</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {error && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nome da agência">
              <input data-testid="create-org-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" placeholder="Acme Promotores" />
            </Field>
            <Field label="Slug (URL)">
              <input data-testid="create-org-slug" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="form-input" placeholder="acme-promotores" />
            </Field>
            <Field label="Cor primária">
              <input data-testid="create-org-color" type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="form-input h-10 p-1" />
            </Field>
            <Field label="Plano">
              <select data-testid="create-org-plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="form-input">
                <option value="trial">Trial</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="business">Business</option>
              </select>
            </Field>
            <Field label="Estado de billing">
              <select data-testid="create-org-billing-status" value={form.billing_status} onChange={(e) => setForm({ ...form, billing_status: e.target.value })} className="form-input">
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="founding">Founding</option>
                <option value="suspended">Suspended</option>
              </select>
            </Field>
            <label className="inline-flex items-center gap-2 text-sm mt-6">
              <input data-testid="create-org-founding" type="checkbox" checked={form.is_founding_customer} onChange={(e) => setForm({ ...form, is_founding_customer: e.target.checked })} />
              Founding customer
            </label>
          </div>
          <div className="border-t border-zinc-100 pt-3 mt-3">
            <div className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-2">Admin inicial</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Email do admin">
                <input data-testid="create-org-admin-email" required type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="form-input" />
              </Field>
              <Field label="Password do admin">
                <input data-testid="create-org-admin-password" required type="text" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="form-input" minLength={6} />
              </Field>
              <Field label="Nome do admin">
                <input data-testid="create-org-admin-name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="form-input" />
              </Field>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-zinc-200 rounded-lg text-sm">Cancelar</button>
            <button data-testid="create-org-submit" type="submit" disabled={saving} className="px-4 py-2 bg-zinc-950 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              {saving ? "A criar…" : "Criar organização"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
